import * as React from 'react';
import { useToast } from '@/context/ToastContext';
import { api, getErrorMessage } from '@/api/api';
import { SOCIAL_PLATFORMS } from '../data';
import type { SocialPlatform } from '../types';
import type { EchoDraftResponse, SocialLinks } from '@/types/echo_creation';

interface UseSocialMediaOptions {
  draftId: string | null;
  draft: EchoDraftResponse | null;
}

/**
 * Manage social platforms linking for a given draft.
 * Persists each change to the backend via PATCH /echoes/:id/social-links.
 */
export const useSocialMedia = ({ draftId, draft }: UseSocialMediaOptions) => {
  const { addToast } = useToast();
  const [platforms, setPlatforms] = React.useState<SocialPlatform[]>(() =>
    SOCIAL_PLATFORMS.map((platform) => ({ ...platform }))
  );
  const [isUpdating, setIsUpdating] = React.useState(false);
  const hydratedRef = React.useRef(false);

  // Hydrate platforms from draft.social_links on first load
  React.useEffect(() => {
    if (hydratedRef.current || !draft?.social_links) return;
    const links = draft.social_links;
    setPlatforms((prev) =>
      prev.map((platform) => {
        const url = links[platform.id as keyof SocialLinks];
        return url ? { ...platform, isConnected: true, value: url } : platform;
      })
    );
    hydratedRef.current = true;
  }, [draft]);

  const buildPayload = (updated: SocialPlatform[]): SocialLinks => {
    const payload: SocialLinks = {};
    for (const platform of updated) {
      payload[platform.id as keyof SocialLinks] = platform.isConnected
        ? (platform.value ?? '')
        : '';
    }
    return payload;
  };

  const handleConnect = async (platformId: string, value?: string) => {
    if (!value?.trim()) return;

    const updated = platforms.map((platform) =>
      platform.id === platformId
        ? { ...platform, isConnected: true, value: value.trim() }
        : platform
    );
    setPlatforms(updated);

    if (!draftId) return;

    setIsUpdating(true);
    try {
      await api.echoes.updateSocialLinks(draftId, buildPayload(updated));
      addToast({ title: 'Saved', subtitle: `${platformId} link saved.`, type: 'success' });
    } catch (err) {
      // Revert on failure
      setPlatforms((prev) =>
        prev.map((platform) =>
          platform.id === platformId ? { ...platform, isConnected: false, value: '' } : platform
        )
      );
      addToast({ title: 'Error', subtitle: getErrorMessage(err), type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDisconnect = async (platformId: string) => {
    const previous = platforms.find((platform) => platform.id === platformId);
    const updated = platforms.map((platform) =>
      platform.id === platformId ? { ...platform, isConnected: false, value: '' } : platform
    );
    setPlatforms(updated);

    if (!draftId) return;

    setIsUpdating(true);
    try {
      await api.echoes.updateSocialLinks(draftId, buildPayload(updated));
      addToast({ title: 'Removed', subtitle: 'Social link removed.', type: 'info' });
    } catch (err) {
      // Revert on failure
      if (previous) {
        setPlatforms((prev) =>
          prev.map((platform) =>
            platform.id === platformId
              ? { ...platform, isConnected: true, value: previous.value ?? '' }
              : platform
          )
        );
      }
      addToast({ title: 'Error', subtitle: getErrorMessage(err), type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return { platforms, handleConnect, handleDisconnect, isUpdating };
};
